import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  hydratedGlobalParams,
  mergeEnvToBaseUrls,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

/** Matches `WorkspaceHeaderData` / AngularJS bridge shape expected by child CE inputs. */
interface LocalChartHeaderBridge {
  globalParams: Record<string, unknown>;
  userSettingsData: Record<string, unknown>;
  serverConfigData: Record<string, unknown>;
  generalConfigData: Record<string, unknown>;
}

/**
 * Dispatcher that routes to the appropriate sub-component based on TRANS_TYPE_ACTUAL:
 *   LOCAL_BOOK_ADJ / _CY / _PY  -> ReclassLocalChartComponent
 *   LOCAL_TAX_ADJ_PERM           -> EpAdjsLocalChartComponent (trans-type=LOCAL_TAX_ADJ_PERM)
 *   LOCAL_EP_ADJ / LOCAL_TAX_ADJ -> EpAdjsLocalChartComponent (trans-type=LOCAL_EP_ADJ)
 *   LOCAL_ELIM                   -> ElimReclassLocalComponent
 */
@Component({
    selector: 'gtw-all-local-chart-adjustments',
    templateUrl: './all-local-chart-adjustments.component.html',
    styleUrls: ['./all-local-chart-adjustments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AllLocalChartAdjustmentsComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private readonly contextSubs = new Subscription();

  modalTitle = 'Local Chart Adjustments';
  minimise = false;
  showFormLoader = true;
  body = '';
  componentType: 'message' | 'reclass' | 'tax-perm' | 'ep' | 'elim' = 'message';

  baseURLsString = '';
  filterParamsString = '';
  headerDataString = '';
  rowDataString = '';

  private rowData: any = {};
  private globalParams: any = {};
  private GENERAL_CONFIG: any = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private globalService: GlobalService,
    private workspaceService: WorkspaceService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndApply();
    this.contextSubs.add(
      this.workspaceService.setTabActive.subscribe(() => this.resetAndApply())
    );
    this.contextSubs.add(
      this.globalService.globalParamsChanged$.subscribe(() => this.resetAndApply())
    );
  }

  private resetAndApply(): void {
    this.isInitialized = false;
    this.showFormLoader = true;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  /**
   * Build row/header/base-url JSON strings for nested CEs from GlobalService, WorkspaceService, and AppService.
   */
  private applyWorkspaceContext(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};

    let row = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(row)) {
      row = row.length ? extractObject(row[0]) : {};
    } else {
      row = extractObject(row);
    }
    this.rowData = Object.keys(row).length ? row : {};

    const fp = normalizeFilterParams(activeScreen);
    this.filterParamsString = JSON.stringify(fp);

    const shell = buildHeaderDataShell(this.globalService, this.appService);
    const hydrated = hydratedGlobalParams(this.globalService, this.workspaceService) || {};
    this.globalParams = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.GENERAL_CONFIG = { ...shell.generalConfigData };

    this.buildChildInputStrings(shell);
  }

  /**
   * Serializes inputs for nested CEs (`[row-data]`, `[header-data]`, `[base-urls]`).
   * Previously `rowDataString` / `headerDataString` were never set, so speed-up / workspace routing broke the child tree.
   */
  private buildChildInputStrings(shell: {
    generalConfigData: Record<string, unknown>;
    userSettingsData: unknown;
    serverConfigData: unknown;
  }): void {
    this.rowDataString = Object.keys(this.rowData).length ? JSON.stringify(this.rowData) : '';

    const headerPayload: LocalChartHeaderBridge = {
      globalParams: { ...this.globalParams },
      userSettingsData: extractObject(shell.userSettingsData || {}),
      serverConfigData: extractObject(shell.serverConfigData || {}),
      generalConfigData: { ...shell.generalConfigData }
    };
    this.headerDataString = JSON.stringify(headerPayload);

    const mergedBase = mergeEnvToBaseUrls(this.appService.baseURLs || {}, shell.generalConfigData);
    const env: any = shell.generalConfigData;
    const baseURLs: GtwElementsBaseUrlsModel = {
      admin: String(mergedBase.admin ?? ''),
      api: String(mergedBase.api ?? ''),
      custom: String(mergedBase.custom ?? ''),
      efile: String(mergedBase.efile ?? ''),
      pdfEngine: String(mergedBase.pdfEngine ?? env.pdf_engine_api_url ?? ''),
      bulkPdfEngine: String(mergedBase.bulkPdfEngine ?? env.bulk_pdf_engine_api_url ?? '')
    };
    this.baseURLsString = JSON.stringify(baseURLs);
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalParams || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    if (!this.rowDataString || !this.headerDataString || !this.baseURLsString) return;
    this.isInitialized = true;
    this.determineComponentType();
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
  }

  private determineComponentType(): void {
    switch (this.rowData.TRANS_TYPE_ACTUAL) {
      case 'LOCAL_BOOK_AMT':
        this.componentType = 'message';
        this.body = 'This modal does not work for Book Amount.';
        this.modalTitle = 'Book Amount';
        break;
      case 'LOCAL_BOOK_ADJ': case 'LOCAL_BOOK_ADJ_CY': case 'LOCAL_BOOK_ADJ_PY':
        this.componentType = 'reclass';
        this.modalTitle = 'Book Adjustment';
        break;
      case 'LOCAL_TAX_ADJ_PERM':
        this.componentType = 'tax-perm';
        this.modalTitle = 'CY Local Tax Adjustment Perm';
        break;
      case 'LOCAL_EP_ADJ': case 'LOCAL_TAX_ADJ': case 'LOCAL_TAX_ADJ_GHOST': case 'LOCAL_TAX_ADJ_TEMP':
        this.componentType = 'ep';
        this.modalTitle = 'CY Local Tax Adjustment';
        break;
      case 'LOCAL_ELIM':
        this.componentType = 'elim';
        this.modalTitle = 'Elimination Reclass Summary View';
        break;
      default:
        this.componentType = 'message';
        this.body = `Unknown transaction type: ${this.rowData.TRANS_TYPE_ACTUAL}`;
    }
  }

  onChildRequestGridUpdate(event: string): void { this.requestGridUpdate.emit(event); }

  cancel(): void { this.requestGridUpdate.emit('cancel'); }
}
